<template>
  <section class="c_quote_slider_2022 c_agile_slider_2022 c_wrapper c_quote_slider_theme" :class="[themeClass]">
    <div :class="['c_quote_slider--' + textAlignment]">
      <RelativeAnchor :id="pageTitle" :block-id="blockId" />
      <agile
        @after-change="getCurrentSlide($event)"
        :options="agileOptions"
        class="c_agile_slider_2022 c_slider--arrow_buttons agile--new-navigation"
        v-if="displaySlider"
      >
        <Quote
          v-for="(slide, index) in slidesFiltered"
          :key="index"
          class="slide"
          :slide="slide"
          :theme="theme"
          :media-type="mediaType"
        />
      </agile>
      <SlideNumbers v-if="displaySlider" :number-of-slides="numberOfSlides" :current-slide="currentSlide" />
      <Quote v-if="!displaySlider" class="slide" :slide="slidesFiltered[0]" :theme="theme" :media-type="mediaType" />
    </div>
    <div class="c_quote_slider_media" v-if="backgroundAsset">
      <div class="c_quote_slider_media__overlay" :style="{opacity: backgroundOpacity}"></div>
      <MediaImage background :sources="[backgroundAsset.lpImageTextUncropped]" v-if="mediaType === 'image' && theme === 'background'" />
    </div>
  </section>
</template>

<script>
import { calculateOpacity } from 'JS/global.js';
import { defineComponent, toRefs, reactive } from '@nuxtjs/composition-api';
import { VueAgile } from 'vue-agile';
import SlideNumbers from 'Components/slider/SlideNumbers.vue';
import Quote from 'Components/2022_quote_slider/Quote.vue';
import MediaImage from 'Components/image/MediaImage.vue';

export default defineComponent({
  name: 'QuoteSliderV2',
  components: {
    RelativeAnchor: () => import('Components/relative_anchor/RelativeAnchor.vue'),
    SlideNumbers,
    agile: VueAgile,
    Quote,
    MediaImage,
  },
  data() {
    return {
      numberOfSlides: null,
      currentSlide: null,
      agileOptions: {
        infinite: true,
        slidesToShow: 1,
        navButtons: true,
        fade: true,
        dots: false,
      },
      displaySlider: false,
      slidesFiltered: [],
    };
  },
  props: {
    blockId: String,
    pageTitle: String,
    slides: Array,
    textAlignment: {
      type: String,
      default: 'left',
    },
    backgroundOverlayOpacity: String,
    backgroundImage: Array,
    theme: String,
  },
  methods: {
    getCurrentSlide(e) {
      return (this.currentSlide = e.currentSlide + 1);
    },
  },
  created() {
    this.slides.forEach((slide) => {
      if (slide.enableQuote) {
        this.slidesFiltered.push(slide);
      }
    });
    this.numberOfSlides = this.slidesFiltered.length;
    if (this.numberOfSlides > 1) {
      this.displaySlider = true;
    }
  },
  setup(props) {
    const state = reactive({
      themeClass: 'c_quote_slider_theme--white',
      backgroundOpacity: 0,
      mediaType: '',
    });

    const backgroundAsset = props.backgroundImage?.[0];
    state.mediaType = backgroundAsset ? 'image' : 'none';

    const themes = {
      white: 'c_quote-slider_theme--white',
      black: 'c_quote-slider_theme--black',
      lightGray: 'c_quote-slider_theme--lightGray',
      yellow: 'c_quote-slider_theme--yellow',
      background: 'c_quote-slider_theme--background',
    };
    state.themeClass = props.theme ? themes[props.theme] : 'c_quote-slider_theme--white';

    if (props.theme === 'background') state.backgroundOpacity = calculateOpacity(props.backgroundOverlayOpacity);

    return { ...toRefs(state), backgroundAsset };
  },
});
</script>

<style lang="scss">
.c_quote_slider_2022 {
  position: relative;
  margin: 0;
  max-width: 100%;
  padding-top: px_to_rem(60);
  padding-bottom: px_to_rem(60);
  &.c_wrapper {
    padding-left: px_to_rem(40);
    padding-right: px_to_rem(40);
    @include mq($mq_sm_to_md) {
      padding-left: px_to_rem(100);
      padding-right: px_to_rem(100);
    }
  }
  .c_quote_slider {
    $self: &;
    @at-root #{$self}--left {
      text-align: left;
      z-index: 10;
      position: relative;
    }
    @at-root #{$self}--center {
      text-align: center;
      z-index: 10;
      position: relative;

      .agile__nav-button {
        height: 50px;
        width: 50px;
      }
    }
    @at-root #{$self}--right {
      text-align: right;
      z-index: 10;
      position: relative;
    }

    .c_slider {
      --slider-dots-default-color: #{$color_gray_112};
    }
    .c_box--yellow & .c_slider {
      --slider-dots-default-color: #{$color_gray_112};
      --slider-dots-active-color: #{$color_white};
    }

    @at-root #{$self}__mask {
      background: rgba($color_black_raw, 0.75);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 25;
    }
  }
  .c_slider__slide_numbers {
    $number: &;
    font-size: 1rem;
    justify-content: center;
    margin-top: 0.5rem;
    font-family: 'Graphik-Regular';

    @include mq($mq_sm_to_md) {
      margin-top: -2rem;
    }

    .c_slider__number_divider {
      padding: 0 px_to_rem(4);
      color: inherit;
    }
  }
}
.c_quote-slider_theme {
  .agile__actions .agile__nav-button.agile__nav-button--prev,
  .agile__actions .agile__nav-button.agile__nav-button--next {
    background-color: #f6f6f6;
    border: 1px solid #dddddd;
    color: black;

    &:hover {
      background-color: #ddd;
    }
    &:active {
      background-color: #b2b2b2;
      border: 1px solid #b2b2b2;
    }
  }

  $theme: &;
  @at-root #{$theme}--white {
    color: $color_black;
    background-color: $color_white;

    a {
      color: $color_red;

      &:visited {
        color: $color_red;
      }
      svg {
        stroke {
          color: $color_red;
        }
      }
    }

    .agile__actions .agile__nav-button.agile__nav-button--prev,
    .agile__actions .agile__nav-button.agile__nav-button--next {
      background-color: #f6f6f6;
      border: 1px solid #dddddd;
      color: black;

      &:hover {
        background-color: #ddd;
      }
      &:active {
        background-color: #b2b2b2;
        border: 1px solid #b2b2b2;
      }
    }
  }
  @at-root #{$theme}--lightGray {
    color: $color_black;
    background-color: $color_gray_235;

    a {
      color: $color_black;

      &:visited {
        color: $color_black;
      }
    }

    .agile__actions .agile__nav-button.agile__nav-button--prev,
    .agile__actions .agile__nav-button.agile__nav-button--next {
      background-color: #393939;
      border: 1px solid #555555;
      color: white;

      &:hover {
        background-color: #717171;
        border: 1px solid #717171;
      }
      &:active {
        background-color: #b2b2b2;
        border: 1px solid #b2b2b2;
      }
    }
  }
  @at-root #{$theme}--black {
    color: $color_white;
    background-color: $color_black;

    a {
      color: $color_yellow;
      &:visited {
        color: $color_yellow;
      }
    }

    .agile__actions .agile__nav-button.agile__nav-button--prev,
    .agile__actions .agile__nav-button.agile__nav-button--next {
      background-color: #393939;
      border: 1px solid #555555;
      color: white;

      &:hover {
        background-color: #717171;
        border: 1px solid #717171;
      }
      &:active {
        background-color: #b2b2b2;
        border: 1px solid #b2b2b2;
      }
    }
  }
  @at-root #{$theme}--yellow {
    color: $color_black;
    background-color: $color_yellow;

    a {
      color: $color_black;
      &:visited {
        color: $color_black;
      }
    }

    .agile__actions .agile__nav-button.agile__nav-button--prev,
    .agile__actions .agile__nav-button.agile__nav-button--next {
      background-color: #393939;
      border: 1px solid #555555;
      color: white;

      &:hover {
        background-color: #717171;
        border: 1px solid #717171;
      }
      &:active {
        background-color: #b2b2b2;
        border: 1px solid #b2b2b2;
      }
    }
  }
}
.c_quote_slider_media {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: 100%;
  z-index: 5;
  display: flex;
  align-items: center;
  video,
  img {
    object-fit: cover;
    min-height: 100%;
  }
}
.c_quote_slider_media__overlay {
  background-color: rgba(0, 0, 0, 1);
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  z-index: 6;
}

.agile--new-navigation.c_agile_slider_2022.agile {
  .agile__actions {
    margin: 2.5rem 0 0 0;
    justify-content: center;
    gap: 6rem;
  }
  .agile__actions .agile__nav-button.agile__nav-button--prev,
  .agile__actions .agile__nav-button.agile__nav-button--next {
    margin: 0;
    font-size: 16px;
    height: 36px;
    width: 36px;
    position: relative;
    top: 0;
    left: 0;
    transform: none;
    transition: all 0.3s;
  }
}
</style>
